import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Stethoscope,
  Users,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  X,
  Sparkles,
  UserPlus,
  CalendarPlus,
  FileText,
  Bot,
  Activity,
  MessageSquare,
  BadgeCheck,
  ClipboardList,
  Wallet,
  BellRing,
  type LucideIcon,
} from "lucide-react";

type WalkRole = "doctor" | "staff";

type WalkStep = {
  icon: LucideIcon;
  title: string;
  desc: string;
  actions: { label: string; href: string; primary?: boolean }[];
};

const DOCTOR_STEPS: WalkStep[] = [
  {
    icon: Stethoscope,
    title: "Your consultation cockpit",
    desc: "One screen for history, vitals, AI-assisted diagnosis, and a signed prescription in under a minute.",
    actions: [
      { label: "Open composer", href: "/prescriptions/new", primary: true },
      { label: "See templates", href: "/templates" },
    ],
  },
  {
    icon: FileText,
    title: "Specialty templates & AI diagnosis",
    desc: "One-click packs for T2DM, HTN, OB/GYN, PCOS — plus AI differential and drug-interaction safety.",
    actions: [
      { label: "Browse templates", href: "/templates", primary: true },
      { label: "AI diagnosis details", href: "/ai/diagnosis-ai" },
    ],
  },
  {
    icon: Bot,
    title: "Let AI run the busywork",
    desc: "WhatsApp booking agent, Bengali ambient scribe, and care-gap reminders — all working while you see patients.",
    actions: [
      { label: "Open AI Suite", href: "/#ai-suite", primary: true },
      { label: "Care gaps", href: "/care-gaps" },
    ],
  },
  {
    icon: BadgeCheck,
    title: "Publish your doctor profile",
    desc: "Get discovered on Google with your public profile, reviews, and articles — powered by DoctorsX SEO.",
    actions: [
      { label: "Edit public profile", href: "/public-profile", primary: true },
      { label: "Read reviews", href: "/reviews" },
    ],
  },
];

const STAFF_STEPS: WalkStep[] = [
  {
    icon: CalendarPlus,
    title: "Own the appointment book",
    desc: "Book, reschedule, and manage today's queue — with live serial numbers and WhatsApp confirmations.",
    actions: [
      { label: "Open appointments", href: "/appointments", primary: true },
      { label: "Waiting queue", href: "/waiting-queue" },
    ],
  },
  {
    icon: UserPlus,
    title: "Register patients in seconds",
    desc: "Capture patient details once — history, vitals, and documents follow them across every visit.",
    actions: [
      { label: "Add a patient", href: "/patients", primary: true },
      { label: "Patient portal", href: "/patient-portal" },
    ],
  },
  {
    icon: Wallet,
    title: "Billing, invoices & daily income",
    desc: "bKash, Nagad, card, cash — one-tap invoicing and the daily income register (হিসাব খাতা).",
    actions: [
      { label: "Open billing", href: "/billing", primary: true },
      { label: "Daily income", href: "/daily-income" },
    ],
  },
  {
    icon: MessageSquare,
    title: "Talk to patients on WhatsApp",
    desc: "Send reminders, follow-ups, and campaigns using bilingual templates — Twilio + WhatsApp fallback built-in.",
    actions: [
      { label: "Communications", href: "/communications", primary: true },
      { label: "AI Inbox", href: "/ai-agent/inbox" },
    ],
  },
];

const ROLE_META: Record<WalkRole, { label: string; kicker: string; icon: LucideIcon; accent: [string, string] }> = {
  doctor: { label: "Doctor", kicker: "Clinical workflow", icon: Stethoscope, accent: ["#1ba179", "#7c3aed"] },
  staff: { label: "Staff / Receptionist", kicker: "Front-desk workflow", icon: Users, accent: ["#059669", "#1ba179"] },
};

const storageKey = (uid: string) => `dx:role_walkthrough:v1:${uid}`;

export default function RoleWalkthrough() {
  const { user } = useAuth();
  const { isAdmin, isStaff, isLoading: rolesLoading } = useUserRoles();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<WalkRole | null>(null);
  const [step, setStep] = useState<number>(-1); // -1 = role picker, 0..n = steps, n+1 = done
  const [ready, setReady] = useState(false);

  // Decide whether to show the walkthrough on mount
  useEffect(() => {
    if (!user || rolesLoading) return;
    const seen = localStorage.getItem(storageKey(user.id));
    if (seen) return;
    (async () => {
      // Wait for onboarding to be complete so we don't stack dialogs
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_completed")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!data?.onboarding_completed) return;
      // Auto-detect role: admin (owner) → doctor; explicit staff → staff; both → let user pick
      let inferred: WalkRole | null = null;
      if (isAdmin && !isStaff) inferred = "doctor";
      else if (isStaff && !isAdmin) inferred = "staff";
      setRole(inferred);
      setStep(inferred ? 0 : -1);
      setReady(true);
      setOpen(true);
    })();
  }, [user, rolesLoading, isAdmin, isStaff]);

  const steps = useMemo(() => (role === "staff" ? STAFF_STEPS : DOCTOR_STEPS), [role]);
  const total = steps.length;

  const complete = () => {
    if (user) localStorage.setItem(storageKey(user.id), new Date().toISOString());
    setOpen(false);
  };

  if (!ready || !open) return null;

  const isPicker = step === -1;
  const current = !isPicker ? steps[step] : null;
  const meta = role ? ROLE_META[role] : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && complete()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0 border-0 shadow-2xl">
        {/* Header band */}
        <div
          className="relative px-8 pt-8 pb-6 text-white"
          style={{
            background: meta
              ? `linear-gradient(135deg, ${meta.accent[0]}, ${meta.accent[1]})`
              : "linear-gradient(135deg, #0f172a, #1ba179)",
          }}
        >
          <button
            onClick={complete}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center transition"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/15 text-[10.5px] font-semibold uppercase tracking-wider mb-3">
            <Sparkles className="w-3 h-3" /> Welcome to DoctorsX
          </div>
          {isPicker ? (
            <>
              <h2 className="text-2xl md:text-3xl font-bold leading-tight">How will you be using DoctorsX?</h2>
              <p className="mt-2 text-[13.5px] text-white/80">
                Pick a role so we can show you the most relevant next steps.
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[11.5px] text-white/80 mb-2">
                {meta && <meta.icon className="w-3.5 h-3.5" />}
                <span>{meta?.label} · {meta?.kicker}</span>
                <span className="ml-auto font-mono">
                  {step + 1} / {total}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold leading-tight">{current?.title}</h2>
              <div className="mt-4 flex gap-1.5">
                {steps.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full ${i <= step ? "bg-white" : "bg-white/25"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Body */}
        <div className="p-8 bg-white">
          {isPicker ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(Object.keys(ROLE_META) as WalkRole[]).map((r) => {
                const m = ROLE_META[r];
                const Icon = m.icon;
                return (
                  <button
                    key={r}
                    onClick={() => {
                      setRole(r);
                      setStep(0);
                    }}
                    className="group text-left p-5 rounded-2xl border border-black/10 hover:border-transparent hover:shadow-lg transition relative overflow-hidden"
                  >
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition"
                      style={{ background: `linear-gradient(135deg, ${m.accent[0]}08, ${m.accent[1]}08)` }}
                    />
                    <div className="relative">
                      <div
                        className="w-12 h-12 rounded-xl grid place-items-center text-white mb-4 shadow-md"
                        style={{ background: `linear-gradient(135deg, ${m.accent[0]}, ${m.accent[1]})` }}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="text-[16px] font-bold text-[#0f172a] mb-1">{m.label}</div>
                      <div className="text-[12.5px] text-[#0f172a]/60 leading-relaxed">
                        {r === "doctor"
                          ? "Consultations, prescriptions, AI scribe, public profile."
                          : "Bookings, patients, billing, WhatsApp communications."}
                      </div>
                    </div>
                  </button>
                );
              })}
              <button
                onClick={complete}
                className="sm:col-span-2 text-[12px] text-[#0f172a]/50 hover:text-[#0f172a] mt-2"
              >
                Skip walkthrough
              </button>
            </div>
          ) : current ? (
            <div>
              <p className="text-[15px] text-[#0f172a]/75 leading-relaxed">{current.desc}</p>

              <div className="mt-6 rounded-xl bg-[#f8fafc] border border-black/5 p-4">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#0f172a]/50 mb-3 flex items-center gap-1.5">
                  <BellRing className="w-3 h-3" /> Recommended next actions
                </div>
                <div className="flex flex-wrap gap-2">
                  {current.actions.map((a) => (
                    <Link
                      key={a.href}
                      to={a.href}
                      onClick={complete}
                      className={
                        a.primary
                          ? "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[12.5px] font-semibold shadow-sm hover:opacity-95 transition"
                          : "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[#0f172a] text-[12.5px] font-semibold border border-black/10 hover:bg-black/5 transition"
                      }
                      style={
                        a.primary && meta
                          ? { background: `linear-gradient(135deg, ${meta.accent[0]}, ${meta.accent[1]})` }
                          : undefined
                      }
                    >
                      {a.label} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Footer nav */}
              <div className="mt-6 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => (step === 0 ? setStep(-1) : setStep(step - 1))}
                  className="text-[#0f172a]/60"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {step === 0 ? "Change role" : "Back"}
                </Button>
                <div className="flex items-center gap-2">
                  <button onClick={complete} className="text-[12px] text-[#0f172a]/50 hover:text-[#0f172a] px-3 py-2">
                    Skip
                  </button>
                  {step < total - 1 ? (
                    <Button size="sm" onClick={() => setStep(step + 1)}>
                      Next <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button size="sm" onClick={complete} className="bg-[#059669] hover:bg-[#047857]">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Finish
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Unused imports kept intentionally minimal
void Activity; void ClipboardList;
